import {
  ThemePreset,
  normalizeThemePreset,
  presetAssetUrl,
} from './themePreset';
import {
  MARKETING_EMAIL,
  MARKETING_PHONE,
  MARKETING_PHONE_HUMAN,
} from './marketingSite';

/**
 * The identity a theme preset brings with it, beyond colours: the name in the
 * tab, the page description and share tags, the icons, the web-app manifest,
 * and the contact details and name the portal's own copy uses.
 *
 * `default` has none, and every caller treats "no preset brand" as "leave the
 * page exactly as it is today" - which is what keeps Go Oil (and every tenant
 * without a preset) byte-identical.
 *
 * The Aperio values are the marketing site's own (aperio/site/build/shell.js
 * head tags, build/meta-files.js manifest, build/util.js contact), so the tab,
 * the icon and the browser chrome do not change across the hand-off. The icon
 * and share-image files under public/aperio/ are byte copies of the site's,
 * checked by scripts/check-aperio-assets.js.
 *
 * Keep public/index.html's inline Aperio head script and the server's bot
 * preview (tenantHeaderMiddleware.ts, PRESET_PREVIEW) in step with this.
 */
export type PresetBrand = {
  /** The brand as it reads in a title and in running copy. */
  name: string;
  /** `<meta name="description">`, og:description, twitter:description. */
  description: string;
  themeColor: string;
  /** Paths under PUBLIC_URL. */
  icon: string;
  iconType: string;
  appleTouchIcon: string;
  manifest: string;
  shareImage: string;
  shareImageAlt: string;
  /** Customer-facing contact, as the marketing site prints it. */
  email: string;
  phone: string;
  phoneHuman: string;
};

const PRESET_BRANDS: Partial<Record<ThemePreset, PresetBrand>> = {
  aperio: {
    name: 'Aperio',
    description: 'Book Aperio mobile car maintenance in Canada & the US.'
      + ' A certified technician comes to your driveway when service is truly'
      + ' due.',
    themeColor: '#0B2B3C',
    icon: 'aperio/favicon.svg',
    iconType: 'image/svg+xml',
    appleTouchIcon: 'aperio/apple-touch-icon.png',
    manifest: 'aperio/manifest.webmanifest',
    shareImage: 'aperio/og-default.jpg',
    shareImageAlt: 'An Aperio mobile maintenance van parked in a driveway',
    email: MARKETING_EMAIL,
    phone: MARKETING_PHONE,
    phoneHuman: MARKETING_PHONE_HUMAN,
  },
};

/** The preset's brand; null for the default preset and anything unknown. */
export function presetBrand(themePreset?: string | null): PresetBrand | null {
  return PRESET_BRANDS[normalizeThemePreset(themePreset)] ?? null;
}

/** The tab title every booking route carries. */
export function bookingTitle(brandName: string): string {
  return `Book Online | ${brandName}`;
}

/**
 * The tenant config bootTheme stored on `window.__TENANT__`, for code that
 * runs outside React (error-message helpers). Undefined before boot.
 */
function bootedThemePreset(): string | null | undefined {
  try {
    type Booted = { __TENANT__?: { themePreset?: string | null } };
    // eslint-disable-next-line no-underscore-dangle
    return (window as unknown as Booted).__TENANT__?.themePreset;
  } catch {
    return undefined;
  }
}

/** presetBrand() for the tenant this page booted as. */
export function currentPresetBrand(): PresetBrand | null {
  return presetBrand(bootedThemePreset());
}

/**
 * "Go Oil Winnipeg" -> "Aperio Winnipeg". Aperio bookings are served by Go
 * Oil's franchises, whose names carry the Go Oil prefix. Unchanged without a
 * preset brand.
 */
export function brandFranchiseName(
  name: string | null | undefined,
  brand: PresetBrand | null,
): string {
  const value = name || '';
  if (!brand || !/^\s*go\s?oil\b/i.test(value)) return value;
  const rest = value.replace(/^\s*go\s?oil\b[\s\-–—:]*/i, '');
  return `${brand.name} ${rest}`.trim();
}

/** A Go Oil inbox becomes the brand's own address; unchanged otherwise. */
export function brandContactEmail(
  email: string | null | undefined,
  brand: PresetBrand | null,
): string {
  const value = email || '';
  if (!brand || !/@gooil\.(ca|app|us|com)\s*$/i.test(value)) return value;
  return brand.email;
}

/** Absolute URL of one of the preset's public files (for share tags). */
export function presetAbsoluteUrl(path: string): string {
  const url = presetAssetUrl(path);
  if (/^https?:\/\//i.test(url)) return url;
  try {
    return new URL(url, window.location.origin).toString();
  } catch {
    return url;
  }
}
